import { html, render, svg, type TemplateResult } from 'lit-html';
import styles from './pk-activity-chart.css?inline';
import { withCsrf } from '../../lib/csrf.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Bucket {
    tsUtc:  string;
    count:  number;
}

type ChartState = 'idle' | 'loading' | 'ok' | 'error';

// ---------------------------------------------------------------------------
// SVG dot-plot rendering
// ---------------------------------------------------------------------------

const PAD_LEFT   = 48;
const PAD_RIGHT  = 12;
const PAD_TOP    = 16;
const PAD_BOTTOM = 32;

function renderChart(
    buckets: Bucket[],
    width: number,
    height: number,
    bucketGrain: string,
): TemplateResult {
    if (buckets.length === 0) {
        // Return an empty SVG with a "no data" label
        return svg`
            <svg viewBox="0 0 ${width} ${height}" width="${width}" height="${height}"
                 aria-label="No activity data" role="img">
                <text x="${width / 2}" y="${height / 2}"
                      text-anchor="middle" dominant-baseline="middle"
                      fill="rgba(24,27,34,0.45)"
                      font-size="13"
                      font-family="var(--pk-font-family,'Segoe UI',sans-serif)">
                    No activity
                </text>
            </svg>
        `;
    }

    const chartW = width  - PAD_LEFT - PAD_RIGHT;
    const chartH = height - PAD_TOP  - PAD_BOTTOM;

    const maxCount = Math.max(...buckets.map(b => b.count), 1);

    // X: evenly spaced
    const step    = buckets.length > 1 ? chartW / (buckets.length - 1) : chartW;
    const dotR    = Math.max(2, Math.min(6, step / 3));
    const lineClr = 'rgba(14,124,123,0.25)';
    const dotClr  = 'var(--pk-color-primary, #0E7C7B)';
    const axisClr = 'rgba(24,27,34,0.18)';
    const labelClr= 'rgba(24,27,34,0.5)';

    // Y axis: 4 gridlines
    const yTicks = [0, 0.25, 0.5, 0.75, 1].map(t => ({
        y:     PAD_TOP + chartH * (1 - t),
        label: Math.round(maxCount * t).toString(),
    }));

    // X axis labels: show every Nth label to avoid crowding
    const maxLabels  = Math.floor(chartW / 60);
    const labelEvery = Math.max(1, Math.ceil(buckets.length / maxLabels));
    const dateFormat: Intl.DateTimeFormatOptions =
        bucketGrain === 'hour'
            ? { month: 'short', day: 'numeric', hour: 'numeric' }
            : { month: 'short', day: 'numeric' };

    function xPos(i: number) { return PAD_LEFT + (buckets.length === 1 ? chartW / 2 : i * step); }
    function yPos(count: number) { return PAD_TOP + chartH * (1 - count / maxCount); }

    // Build polyline points
    const points = buckets.map((b, i) => `${xPos(i)},${yPos(b.count)}`).join(' ');

    return svg`
        <svg viewBox="0 0 ${width} ${height}"
             width="${width}" height="${height}"
             role="img"
             aria-label="Activity chart — ${buckets.length} ${bucketGrain} buckets, max ${maxCount} per bucket">

            <!-- Grid lines + Y labels -->
            ${yTicks.map(t => svg`
                <line x1="${PAD_LEFT}" y1="${t.y}"
                      x2="${width - PAD_RIGHT}" y2="${t.y}"
                      stroke="${axisClr}" stroke-width="1" />
                <text x="${PAD_LEFT - 6}" y="${t.y}"
                      text-anchor="end" dominant-baseline="middle"
                      fill="${labelClr}" font-size="11"
                      font-family="var(--pk-font-family,'Segoe UI',sans-serif)">
                    ${t.label}
                </text>
            `)}

            <!-- X axis baseline -->
            <line x1="${PAD_LEFT}" y1="${PAD_TOP + chartH}"
                  x2="${width - PAD_RIGHT}" y2="${PAD_TOP + chartH}"
                  stroke="${axisClr}" stroke-width="1" />

            <!-- Connecting line -->
            ${buckets.length > 1 ? svg`
                <polyline points="${points}"
                          fill="none"
                          stroke="${lineClr}"
                          stroke-width="2"
                          stroke-linejoin="round" />
            ` : ''}

            <!-- Dots -->
            ${buckets.map((b, i) => svg`
                <circle cx="${xPos(i)}" cy="${yPos(b.count)}" r="${dotR}"
                        fill="${dotClr}"
                        stroke="none">
                    <title>${new Date(b.tsUtc).toLocaleString(undefined, dateFormat)}: ${b.count}</title>
                </circle>
            `)}

            <!-- X labels -->
            ${buckets.map((b, i) => {
                if (i % labelEvery !== 0 && i !== buckets.length - 1) return '';
                const label = new Date(b.tsUtc).toLocaleString(undefined, dateFormat);
                const textAnchor = i === buckets.length - 1 ? 'end' : 'middle';
                return svg`
                    <text x="${xPos(i)}" y="${PAD_TOP + chartH + 18}"
                          text-anchor="${i === 0 ? 'start' : textAnchor}"
                          fill="${labelClr}" font-size="10"
                          font-family="var(--pk-font-family,'Segoe UI',sans-serif)">
                        ${label}
                    </text>
                `;
            })}
        </svg>
    `;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * `<pk-activity-chart>` — SVG dot-plot of message activity over a time range.
 *
 * Attributes:
 *   `src`    — URL returning `{ buckets: [{ tsUtc, count }] }`.
 *   `width`  — SVG viewBox width in px (default 800).
 *   `height` — SVG viewBox height in px (default 200).
 *   `bucket` — `day` | `hour` (default `day`).
 *
 * Methods:
 *   `load(src?)` — trigger a fetch, optionally overriding src.
 *
 * Events:
 *   none (passive display component; parent drives via `src` or period-change).
 */
export class PkActivityChart extends HTMLElement {
    static readonly tagName = 'pk-activity-chart';

    static get observedAttributes(): string[] {
        return ['src', 'width', 'height', 'bucket'];
    }

    private readonly _root: ShadowRoot;

    private _state: ChartState  = 'idle';
    private _buckets: Bucket[]  = [];
    private _errorMsg: string   = '';

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    get chartWidth():  number { return Number(this.getAttribute('width'))  || 800; }
    get chartHeight(): number { return Number(this.getAttribute('height')) || 200; }
    get bucket():      string { return this.getAttribute('bucket') ?? 'day'; }
    get src():         string { return this.getAttribute('src') ?? ''; }

    connectedCallback(): void {
        if (this.src) {
            this._fetch(this.src);
        } else {
            this._state = 'idle';
            this._doRender();
        }
    }

    attributeChangedCallback(name: string, _old: string | null, next: string | null): void {
        if (name === 'src' && next) {
            this._fetch(next);
        } else {
            this._doRender();
        }
    }

    /** Trigger a fetch, optionally with a new src URL. */
    load(src?: string): void {
        const url = src ?? this.src;
        if (!url) return;
        if (src) this.setAttribute('src', src);
        this._fetch(url);
    }

    private async _fetch(url: string): Promise<void> {
        this._state = 'loading';
        this._doRender();

        try {
            const resp = await fetch(url, withCsrf());
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const data = await resp.json() as { buckets?: Bucket[] };
            this._buckets = data.buckets ?? [];
            this._state   = 'ok';
        } catch (err) {
            this._state    = 'error';
            this._errorMsg = err instanceof Error ? err.message : String(err);
        }
        this._doRender();
    }

    private template(): TemplateResult {
        const w = this.chartWidth;
        const h = this.chartHeight;

        if (this._state === 'loading') {
            return html`
                <style>${styles}</style>
                <div class="chart-container" aria-busy="true">
                    <div class="skeleton" style="height:${h}px;" aria-label="Loading chart"></div>
                </div>
            `;
        }

        if (this._state === 'error') {
            return html`
                <style>${styles}</style>
                <div class="chart-container">
                    <div class="error" role="alert">${this._errorMsg}</div>
                </div>
            `;
        }

        if (this._state === 'idle') {
            return html`<style>${styles}</style>`;
        }

        // 'ok' — may have zero buckets
        return html`
            <style>${styles}</style>
            <div class="chart-container">
                ${this._buckets.length === 0
                    ? html`<div class="empty" style="height:${h}px;" aria-label="No activity data">No activity data for this period.</div>`
                    : renderChart(this._buckets, w, h, this.bucket)
                }
            </div>
        `;
    }

    private _doRender(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkActivityChart.tagName)) {
    customElements.define(PkActivityChart.tagName, PkActivityChart);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-activity-chart': PkActivityChart;
    }
}
